import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, Mail, Lock, Phone, Loader2, ArrowLeft, KeyRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { login, requestRegister, verifyRegister, forgotPassword, resetPassword } from '../services/api';

const AuthPage = ({ mode = 'login' }) => {
  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [registerStep, setRegisterStep] = useState(1); // 1 = form, 2 = verify code
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });

  const [verifyCode, setVerifyCode] = useState('');

  const [resetData, setResetData] = useState({
    email: '',
    code: '',
    new_password: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleResetChange = (e) => {
    setResetData({ ...resetData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let response;
      if (isLogin) {
        response = await login({
          email: formData.email,
          password: formData.password
        });
        loginUser(response.access_token, response.user);
        if (response.user.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/profil');
        }
      } else {
        // Registracija - korak 1: pošalji zahtev
        if (formData.password.length < 6) {
          setError('Lozinka mora imati najmanje 6 karaktera');
          setLoading(false);
          return;
        }
        await requestRegister(formData);
        setSuccess('Verifikacioni kod je poslat na vaš email!');
        setRegisterStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Došlo je do greške');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await verifyRegister(formData.email, verifyCode);
      loginUser(response.access_token, response.user);
      navigate('/profil');
    } catch (err) {
      setError(err.response?.data?.error || 'Neispravan kod');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await forgotPassword(resetData.email);
      setSuccess('Ako email postoji, poslali smo vam kod za reset.');
      setResetStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Greška pri slanju');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await resetPassword(resetData.email, resetData.code, resetData.new_password);
      setSuccess('Lozinka je uspešno promenjena! Možete se prijaviti.');
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetStep(1);
        setResetData({ email: '', code: '', new_password: '' });
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Greška pri promeni lozinke');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <button className="auth-back" onClick={() => { setShowForgotPassword(false); setResetStep(1); setError(''); setSuccess(''); }}>
            <ArrowLeft size={18} />
            Nazad na prijavu
          </button>
          
          <div className="auth-header">
            <h1>Reset lozinke</h1>
            <p>{resetStep === 1 ? 'Unesite email za reset' : 'Unesite kod i novu lozinku'}</p>
          </div>

          {resetStep === 1 ? (
            <form onSubmit={handleForgotPassword} className="auth-form">
              <div className="form-group">
                <label htmlFor="reset-email">
                  <Mail size={16} />
                  Email
                </label>
                <input
                  type="email"
                  id="reset-email"
                  name="email"
                  value={resetData.email}
                  onChange={handleResetChange}
                  required
                  placeholder="vas@email.com"
                />
              </div>

              {error && <p className="auth-error">{error}</p>}
              {success && <p className="auth-success">{success}</p>}

              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? (
                  <><Loader2 size={18} className="loading-spinner" /> Slanje...</>
                ) : (
                  'Pošalji kod'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="auth-form">
              <div className="form-group">
                <label htmlFor="reset-code">
                  <KeyRound size={16} />
                  Kod (6 cifara)
                </label>
                <input
                  type="text"
                  id="reset-code"
                  name="code"
                  value={resetData.code}
                  onChange={handleResetChange}
                  required
                  placeholder="123456"
                  maxLength={6}
                />
              </div>

              <div className="form-group">
                <label htmlFor="new-password">
                  <Lock size={16} />
                  Nova lozinka
                </label>
                <input
                  type="password"
                  id="new-password"
                  name="new_password"
                  value={resetData.new_password}
                  onChange={handleResetChange}
                  required
                  placeholder="Minimum 6 karaktera"
                  minLength={6}
                />
              </div>

              {error && <p className="auth-error">{error}</p>}
              {success && <p className="auth-success">{success}</p>}

              <button type="submit" className="auth-submit" disabled={loading}>
                {loading ? (
                  <><Loader2 size={18} className="loading-spinner" /> Promena...</>
                ) : (
                  'Promeni lozinku'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Verification Step for Registration
  if (!isLogin && registerStep === 2) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <button className="auth-back" onClick={() => { setRegisterStep(1); setError(''); setSuccess(''); setVerifyCode(''); }}>
            <ArrowLeft size={18} />
            Nazad
          </button>
          
          <div className="auth-header">
            <h1>Verifikacija</h1>
            <p>Unesite kod koji smo poslali na {formData.email}</p>
          </div>

          <form onSubmit={handleVerifyRegister} className="auth-form">
            <div className="form-group">
              <label htmlFor="verify-code">
                <KeyRound size={16} />
                Verifikacioni kod
              </label>
              <input
                type="text"
                id="verify-code"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)}
                required
                placeholder="123456"
                maxLength={6}
                style={{ textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }}
              />
            </div>

            {error && <p className="auth-error">{error}</p>}
            {success && <p className="auth-success">{success}</p>}

            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? (
                <><Loader2 size={18} className="loading-spinner" /> Verifikacija...</>
              ) : (
                'Potvrdi'
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <Link to="/" className="auth-back">
          <ArrowLeft size={18} />
          Nazad na početnu
        </Link>
        
        <div className="auth-header">
          <h1>{isLogin ? 'Prijava' : 'Registracija'}</h1>
          <p>{isLogin ? 'Prijavite se na vaš nalog' : 'Kreirajte novi nalog'}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {!isLogin && (
            <div className="form-group">
              <label htmlFor="name">
                <User size={16} />
                Ime i prezime
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required={!isLogin}
                placeholder="Vaše ime"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">
              <Mail size={16} />
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="vas@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              <Lock size={16} />
              Lozinka
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder={isLogin ? 'Vaša lozinka' : 'Minimum 6 karaktera'}
              minLength={isLogin ? undefined : 6}
            />
          </div>

          {!isLogin && (
            <div className="form-group">
              <label htmlFor="phone">
                <Phone size={16} />
                Telefon (opciono)
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+381 60 123 4567"
              />
            </div>
          )}

          {error && <p className="auth-error">{error}</p>}

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? (
              <>
                <Loader2 size={18} className="loading-spinner" />
                {isLogin ? 'Prijavljivanje...' : 'Registracija...'}
              </>
            ) : (
              isLogin ? 'Prijavi se' : 'Registruj se'
            )}
          </button>
        </form>

        {isLogin && (
          <div className="auth-forgot">
            <button onClick={() => setShowForgotPassword(true)}>
              Zaboravili ste lozinku?
            </button>
          </div>
        )}

        <div className="auth-switch">
          {isLogin ? (
            <p>
              Nemate nalog?{' '}
              <button onClick={() => setIsLogin(false)}>Registrujte se</button>
            </p>
          ) : (
            <p>
              Već imate nalog?{' '}
              <button onClick={() => setIsLogin(true)}>Prijavite se</button>
            </p>
          )}
        </div>

        <div className="auth-guest">
          <p>Možete poručiti i bez registracije!</p>
          <Link to="/#products" className="guest-link">Nastavi kao gost</Link>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
